import React, { useState, useCallback } from "react";
import styled from "styled-components";

import { Button, Flex, ItemType, useClipboard, Item } from "@thenounproject/lingo-core";
import { AssetExportController, ExportContext } from "@features/asset-export";
import { getUseIcon } from "../kits/AssetActionButton";

const ActionButtonWrapper = styled(Flex).attrs({
  position: "relative",
  alignSelf: "center",
  gap: "4px",
})``;

const ActionButton = styled(Button).attrs({
  className: "action-button",
  themeOverrides: {
    primaryColor: "black",
    primaryColorTint: "white",
  },
  size: "small",
})`
  opacity: 0;

  &:focus,
  &:highlight {
    opacity: 1;
  }
`;

type Props = {
  item: Item;
  linkUrl?: string;
  canEdit?: boolean;
};

const SearchResultActionButtons = ({ item, linkUrl, canEdit = false }: Props) => {
  const { onCopy, hasCopied } = useClipboard(linkUrl);
  const [exportContext, setExportContext] = useState<ExportContext | null>(null);

  const handleUseButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation?.();
      const inspectable = { item, asset: item.asset };
      setExportContext({
        targetInspectable: inspectable,
        selectedInspectables: [inspectable],
        coordinates: { x: e.clientX, y: e.clientY },
        canEdit,
      });
    },
    [item, canEdit]
  );

  const closeExportController = useCallback(() => {
    setExportContext(null);
  }, []);

  function renderUseButton() {
    if (item?.type !== ItemType.asset) return null;
    if (
      getUseIcon(item.asset) === "action.download" &&
      item?.displayProperties?.allowDownload === false
    )
      return null;

    return <ActionButton icon={getUseIcon(item.asset)} onClick={handleUseButtonClick} />;
  }

  function renderLinkButton() {
    if (!linkUrl) return null;

    return (
      <ActionButton
        icon={hasCopied ? "info.checkmark" : "action.link"}
        tintColor={hasCopied ? "success" : undefined}
        onClick={e => {
          e.stopPropagation?.();
          onCopy();
        }}
      />
    );
  }

  return (
    <>
      <AssetExportController context={exportContext} onClose={closeExportController} />
      <ActionButtonWrapper>
        {renderUseButton()}
        {renderLinkButton()}
      </ActionButtonWrapper>
    </>
  );
};

export default SearchResultActionButtons;
